import React, { useEffect, useState } from 'react'
import {sendOAuthRequest, trySilentSignIn} from '../services/authService'
import { Navigate } from "react-router-dom";
import { PLATFORM_URL } from '../config/constants';

/**
 * Checks if the app is running inside an iframe (native tab)
 */
function isInNativeTab() {
  return window.location !== window.parent.location;
}

/**
 * Sends a RequestReauthentication message to the parent platform
 * This should only be called as a last resort after silent auth fails
 * The platform will handle the authentication flow and re-open the tab
 */
function requestReauthenticationFromPlatform() {
  // Guard: Only send RequestReauthentication once per session to prevent loops
  const reauthKey = 'nativeTab_reauthRequested';
  if (sessionStorage.getItem(reauthKey)) {
    console.log('RequestReauthentication already sent this session, skipping to prevent loop');
    return false;
  }
  
  console.log('Sending RequestReauthentication to platform');
  sessionStorage.setItem(reauthKey, 'true');
  
  const message = {
    type: 'RequestReauthentication',
    url: window.location.pathname || '/homepage',
  };
  window.parent.postMessage(JSON.stringify(message), PLATFORM_URL);
  return true;
}

function Login ({ loggedIn, setLoggedIn }){
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // When running inside a native tab (iframe), try silent authentication first
    // This uses the existing Identity session from the platform
    if (isInNativeTab() && !loggedIn) {
      setIsAuthenticating(true);
      
      // Try silent sign-in first - this will work if the user has an active Identity session
      trySilentSignIn()
        .then(user => {
          if (user) {
            // Silent auth succeeded - user is now logged in
            console.log('Silent authentication successful in native tab');
            setIsAuthenticating(false);
            // The App component will detect the user and update loggedIn state
            window.location.reload(); // Reload to pick up the new auth state
          } else {
            // Silent auth failed - request re-authentication from platform
            // But only do this once to prevent infinite loops
            console.log('Silent authentication failed, requesting re-auth from platform');
            const sent = requestReauthenticationFromPlatform();
            if (!sent) {
              // We already requested reauth but still no user - show error
              setAuthError('Authentication failed. Please close this tab and try again.');
            }
            setIsAuthenticating(false);
          }
        })
        .catch(error => {
          console.error('Error during silent authentication:', error);
          // On error, try requesting re-authentication from platform (once)
          const sent = requestReauthenticationFromPlatform();
          if (!sent) {
            setAuthError('Authentication failed. Please close this tab and try again.');
          }
          setIsAuthenticating(false);
        });
    }
  }, [loggedIn]);

  if(loggedIn){
    return <Navigate to='/homepage' replace />;
  }

  // When in a native tab, show appropriate status
  if (isInNativeTab()) {
    if (authError) {
      return (
        <div className={'mainContainer'}>
          <div className={'titleContainer'}>
            <div>Authentication Error</div>
          </div>
          <div className={'inputContainer'}>
            <p>{authError}</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className={'mainContainer'}>
        <div className={'titleContainer'}>
          <div>Authenticating...</div>
        </div>
        <div className={'inputContainer'}>
          <p>{isAuthenticating ? 'Checking authentication status...' : 'Please wait while the platform handles authentication.'}</p>
        </div>
      </div>
    );
  }

  // When NOT in a native tab (standalone mode), show the normal login button
  return (
    <div className={'mainContainer'}>
      <div className={'titleContainer'}>
        <div>Login</div>
      </div>
    
      <div className={'inputContainer'}>
        <input className={'inputButton'} type="button" onClick={sendOAuthRequest} value={'Login with Access Identity'} />
      </div>
    </div>
  )
}

export default Login
